import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAction } from "convex/react";
import { ConvexError } from "convex/values";
import { api } from "@convex/_generated/api";
import { useAdminAuth } from "@/features/admin/hooks/useAdminAuth";
import { AdminShell } from "@/features/admin/shell/AdminShell";
import { AdminPageHeader } from "@/features/admin/shell/AdminPageHeader";
import { BulkImportBoxScoresModal } from "@/features/admin/games/components/BulkImportBoxScoresModal";
import { GamesTable } from "@/features/admin/games/components/GamesTable";
import { ScheduleGameModal } from "@/features/admin/games/components/ScheduleGameModal";
import { useAdminGames } from "@/features/admin/games/hooks/useAdminGames";
import { useAdminTeams } from "@/features/admin/games/hooks/useAdminTeams";
import { Button } from "@/shared/ui/Button";
import { Skeleton } from "@/shared/ui/Skeleton";
import { EmptyState } from "@/shared/ui/EmptyState";
import { useT } from "@/shared/i18n/LocaleContext";
import { notify } from "@/shared/notifications";

export const Route = createFileRoute("/_adminGuarded/admin/games/")({
  component: AdminGamesPage,
});

// BETA-ONLY: season is hardcoded to the current calendar year.
// A season-picker dropdown should replace this once multi-season support is needed.
const CURRENT_SEASON = new Date().getFullYear();

type ImportTodayResult = {
  importedGames: number;
  skippedGames: number;
  addedPlayers: number;
  errors: Array<{ message: string }>;
};

function AdminGamesPage() {
  const t = useT();
  const { state } = useAdminAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const importTodayBoxScores = useAction(api.scraper.importTodayBoxScores);

  const rows = useAdminGames(CURRENT_SEASON);
  const teams = useAdminTeams();
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [isImportingToday, setIsImportingToday] = useState(false);

  const teamOptions = useMemo(
    () => (teams ?? []).map((team) => ({ value: team._id, label: team.name })),
    [teams],
  );

  async function handleImportToday() {
    if (!token || isImportingToday) return;

    setIsImportingToday(true);
    try {
      const result = (await importTodayBoxScores({ token })) as ImportTodayResult;
      const failedCount = result.errors.length;
      if (failedCount > 0 && result.importedGames === 0) {
        notify.error(result.errors[0]?.message ?? t("admin.games.todayImport.fail"));
      } else if (failedCount > 0) {
        notify.warning(
          t("admin.games.todayImport.partial", {
            imported: String(result.importedGames),
            failed: String(failedCount),
            players: String(result.addedPlayers),
          }),
        );
      } else if (result.importedGames === 0) {
        notify.info(
          t("admin.games.todayImport.empty", {
            skipped: String(result.skippedGames),
          }),
        );
      } else {
        notify.success(
          t("admin.games.todayImport.success", {
            games: String(result.importedGames),
            players: String(result.addedPlayers),
          }),
        );
      }
    } catch (e) {
      notify.error(importTodayErrorMessage(e, t("admin.games.todayImport.fail")));
    } finally {
      setIsImportingToday(false);
    }
  }

  return (
    <AdminShell breadcrumbs={[{ label: t("admin.games.title") }]}>
      <AdminPageHeader
        title={t("admin.games.title")}
        subtitle={t("admin.games.subtitle")}
        actions={
          token ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setBulkImportOpen(true)}
              >
                {t("admin.games.bulkImport.openButton")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleImportToday()}
                isLoading={isImportingToday}
                aria-label={t("admin.games.todayImport.button")}
              >
                {t("admin.games.todayImport.button")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setModalOpen(true)}
              >
                {t("admin.games.schedule")}
              </Button>
            </>
          ) : null
        }
      />

      {rows === undefined ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t("admin.games.list.emptyTitle")}
          description={t("admin.games.list.empty")}
          action={
            token ? (
              <Button variant="primary" size="sm" onClick={() => setModalOpen(true)}>
                {t("admin.games.schedule")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <GamesTable rows={rows} teamOptions={teamOptions} />
      )}

      {token ? (
        <ScheduleGameModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          season={CURRENT_SEASON}
          token={token}
        />
      ) : null}
      {token ? (
        <BulkImportBoxScoresModal
          open={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          token={token}
        />
      ) : null}
    </AdminShell>
  );
}

function importTodayErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ConvexError) {
    return (e.data as { message?: string })?.message ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}
